import { z } from 'zod';

import { PRODUCT_IMAGES_MAX } from './media';

/**
 * Contrato da loja (E, RF-A1, RN-E2..RN-E4), entre a site-api, o site e o
 * back-office.
 *
 * Valores em CENTIMOS (inteiros), nunca em euros com virgula. Datas em ISO-8601.
 *
 * Prefixos HTTP:
 *   - `/api/store/...`       site publico (catalogo, carrinho, as minhas encomendas);
 *   - `/api/site/store/...`  back-office (Gestor de Loja, `store.manage`), so em gestao.
 */

// ---------------------------------------------------------------------------
// Catalogo
// ---------------------------------------------------------------------------

/** `apparel` tem tamanhos e pode ter personalizacao; `membership_fee` e a quota (RF-A1). */
export const productKindSchema = z.enum(['merch', 'apparel', 'membership_fee']);
export type ProductKind = z.infer<typeof productKindSchema>;

/**
 * Quem ve e quem compra um produto (F-012): `all` todos; `associates` todos
 * veem, so associados compram; `secret` so associados com sessao o veem (para
 * os outros nao existe). A quota e sempre `all`.
 */
/**
 * As opcoes da quota (F-001). A quota e sempre do ano civil; o plano so diz
 * como foi paga: anual, anual com camisola (tamanho escolhido na compra) ou
 * mensal (cada mes um pagamento; o primeiro ja torna associado no ano).
 */
export const feePlanSchema = z.enum(['annual', 'annual_jersey', 'monthly']);
export type FeePlan = z.infer<typeof feePlanSchema>;

export const productAudienceSchema = z.enum(['all', 'associates', 'secret']);
export type ProductAudience = z.infer<typeof productAudienceSchema>;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'so letras minusculas, numeros e hifenes');

const centsSchema = z.number().int().nonnegative().max(10_000_000);
const pctSchema = z.number().int().min(0).max(100);

/** Uma variante e o que tem stock: um tamanho, ou a variante unica de um produto sem tamanhos. */
export const publicVariantSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  inStock: z.boolean(),
  /** Poucas unidades (3 ou menos): o site diz "ultimas unidades". */
  lowStock: z.boolean(),
  /** Preco proprio desta variante (as opcoes da quota); `null` = o do produto. */
  priceCents: z.number().int().nullable().default(null),
  /** So nas opcoes da quota. */
  feePlan: feePlanSchema.nullable().default(null),
  /** "Anual + camisola": os tamanhos da camisola que se pode escolher. */
  jerseySizes: z
    .array(z.object({ id: z.string().uuid(), label: z.string(), inStock: z.boolean() }))
    .nullable()
    .default(null),
});
export type PublicVariant = z.infer<typeof publicVariantSchema>;

export const publicProductSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  kind: productKindSchema,
  priceCents: z.number().int(),
  /** Desconto de associado neste produto (RN-E3). 0 na quota. */
  associateDiscountPct: z.number().int(),
  personalization: z
    .object({ priceCents: z.number().int() })
    .nullable()
    .describe('nome e numero, com preco adicional e sem devolucao (RF-E3)'),
  /** A primeira foto (a capa), ou `null` sem fotos. */
  imageUrl: z.string().nullable(),
  /** Todas as fotos, pela ordem do Gestor de Loja; a primeira e a capa. */
  images: z.array(z.string()),
  /** `associates`/`secret`: so associados compram (o site diz "Exclusivo associados"). */
  audience: productAudienceSchema,
  variants: z.array(publicVariantSchema),
});
export type PublicProduct = z.infer<typeof publicProductSchema>;

/** Personalizacao de roupa das equipas (RF-E3). Pelo menos um dos dois. */
export const PERSONALIZATION_NAME_MAX = 12;
export const personalizationSchema = z
  .object({
    name: z
      .string()
      .trim()
      .toUpperCase()
      .max(PERSONALIZATION_NAME_MAX)
      .regex(/^[A-ZÀ-Ý .'-]*$/, 'so letras, espacos, ponto, apostrofo e hifen')
      .nullable()
      .default(null)
      .transform((v) => (v === '' ? null : v)),
    number: z.number().int().min(0).max(99).nullable().default(null),
  })
  .refine((p) => p.name !== null || p.number !== null, {
    message: 'indica o nome, o numero ou os dois',
  });
export type Personalization = z.infer<typeof personalizationSchema>;

// ---------------------------------------------------------------------------
// Carrinho e precos
// ---------------------------------------------------------------------------

export const MAX_LINE_QUANTITY = 20;
export const MAX_CART_LINES = 30;

export const cartLineSchema = z.object({
  variantId: z.string().uuid(),
  quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
  personalization: personalizationSchema.nullable().default(null),
  /**
   * So na quota: o jovem para quem e (RN-A9). Quem compra tem de ser o seu
   * encarregado, com a autorizacao ativa. `null` = para a propria conta.
   */
  forAccountId: z.string().uuid().nullable().default(null),
  /** So na quota "Anual + camisola" (F-001): o tamanho (variante) da camisola. */
  jerseyVariantId: z.string().uuid().nullable().default(null),
});
export type CartLine = z.infer<typeof cartLineSchema>;

// ---------------------------------------------------------------------------
// Codigos de oferta
// ---------------------------------------------------------------------------

/** Letras e algarismos dos codigos, sem os que se confundem (0 O 1 I L). */
export const GIFT_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const GIFT_CODE_LENGTH = 8;

/** `ABCD2345` mostra-se `ABCD-2345`. */
export function formatGiftCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * O codigo como a pessoa o escreve: maiusculas ou minusculas, com ou sem hifen
 * e espacos. Vazio = sem codigo.
 */
export const giftCodeInputSchema = z
  .string()
  .max(40)
  .nullable()
  .default(null)
  .transform((v) => (v === null ? null : v.toUpperCase().replace(/[\s-]/g, '')))
  .transform((v) => (v === '' ? null : v));

/**
 * O que o codigo de oferta do carrinho deu:
 *   - `applied`: uma unidade do produto sai a 0 (a personalizacao paga-se);
 *   - `product_not_in_cart`: o codigo e bom, falta escolher o produto (vem em `product`);
 *   - os outros: o codigo nao serve, e a encomenda e recusada enquanto la estiver.
 */
export const giftStatusSchema = z.enum([
  'applied',
  'product_not_in_cart',
  'sign_in',
  'not_found',
  'not_yours',
  'used',
  'expired',
  'product_unavailable',
]);
export type GiftStatus = z.infer<typeof giftStatusSchema>;

export const quoteGiftSchema = z.object({
  code: z.string(),
  status: giftStatusSchema,
  /** O produto oferecido, para o escolher no carrinho (pode nao estar a venda na loja). */
  product: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      imageUrl: z.string().nullable(),
      personalization: z.object({ priceCents: z.number().int() }).nullable(),
      variants: z.array(publicVariantSchema),
    })
    .nullable(),
  expiresAt: z.string().nullable(),
});
export type QuoteGift = z.infer<typeof quoteGiftSchema>;

export const quoteRequestSchema = z.object({
  /** Pode vir vazio: um carrinho vazio ainda mostra o que um codigo de oferta da. */
  lines: z.array(cartLineSchema).max(MAX_CART_LINES),
  giftCode: giftCodeInputSchema,
});
export type QuoteRequest = z.infer<typeof quoteRequestSchema>;

/** Nivel de quem compra, visto pela loja. `guest` = sem sessao (so no orcamento). */
export const customerLevelSchema = z.enum(['guest', 'user', 'member', 'associate']);
export type CustomerLevel = z.infer<typeof customerLevelSchema>;

export const pricedLineSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid(),
  productKind: productKindSchema,
  productName: z.string(),
  variantLabel: z.string(),
  quantity: z.number().int(),
  unitPriceCents: z.number().int(),
  /** Percentagem aplicada (0 se quem compra nao e associado). */
  discountPct: z.number().int(),
  unitDiscountCents: z.number().int(),
  personalization: z
    .object({ name: z.string().nullable(), number: z.number().int().nullable() })
    .nullable(),
  /** Por unidade; o desconto de associado nao se aplica a personalizacao. */
  personalizationPriceCents: z.number().int(),
  lineTotalCents: z.number().int(),
  /** Quota paga pelo encarregado: o jovem que fica associado (so o login, RN-A16). */
  forAccountId: z.string().uuid().nullable(),
  forLogin: z.string().nullable(),
  /** Unidade oferecida por um codigo de oferta: o preco todo e desconto. */
  gift: z.boolean(),
  /**
   * Preco especial por unidade, so ao balcao (F-013): substitui o preco do
   * produto (a personalizacao paga-se a parte) e nao leva desconto de associado.
   */
  specialPrice: z
    .object({ unitPriceCents: z.number().int(), reason: z.string() })
    .nullable()
    .default(null),
  /** Na linha da quota: a opcao paga (F-001). */
  feePlan: feePlanSchema.nullable().default(null),
  /** A camisola que vem com a quota "Anual + camisola": a 0 EUR, por levantar. */
  includedInFee: z.boolean().default(false),
});
export type PricedLine = z.infer<typeof pricedLineSchema>;

export const quoteProblemSchema = z.object({
  variantId: z.string().uuid(),
  problem: z.enum([
    'unavailable',
    'out_of_stock',
    'fee_members_only',
    'fee_single',
    'personalization_not_offered',
    'sign_in_for_fee',
    /** A quota para um jovem de quem nao e encarregado, ou sem a autorizacao ativa. */
    'fee_youth_not_guarded',
    /** "Para um jovem" so existe na quota. */
    'for_youth_fee_only',
    /** Produto exclusivo de associados e quem compra nao e associado. */
    'associates_only',
    /** Preco especial (balcao) que nao baixa o preco, ou na quota. */
    'special_price_invalid',
    /** Quota "Anual + camisola" sem tamanho, ou com um tamanho que nao e da camisola. */
    'fee_jersey_size',
  ]),
  /** Quantas ha, quando o problema e o stock. */
  available: z.number().int().nullable(),
});
export type QuoteProblem = z.infer<typeof quoteProblemSchema>;

export const quoteSchema = z.object({
  level: customerLevelSchema,
  lines: z.array(pricedLineSchema),
  subtotalCents: z.number().int(),
  discountCents: z.number().int(),
  totalCents: z.number().int(),
  /** Linhas que nao se podem comprar assim. Uma encomenda com problemas e recusada. */
  problems: z.array(quoteProblemSchema),
  /** O codigo de oferta pedido, ou `null` sem codigo. */
  gift: quoteGiftSchema.nullable(),
});
export type Quote = z.infer<typeof quoteSchema>;

// ---------------------------------------------------------------------------
// Encomendas e pagamento
// ---------------------------------------------------------------------------

export const orderChannelSchema = z.enum(['online', 'in_person']);
export type OrderChannel = z.infer<typeof orderChannelSchema>;

export const orderStatusSchema = z.enum(['pending_payment', 'paid', 'delivered', 'cancelled']);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

/** `mbway` e `cash` tambem ao balcao (RN-E4); `multibanco` e `card` so online. */
export const paymentMethodSchema = z.enum(['mbway', 'multibanco', 'card', 'cash']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;
export const onlinePaymentMethodSchema = z.enum(['mbway', 'multibanco', 'card']);
export const inPersonPaymentMethodSchema = z.enum(['mbway', 'cash']);

/** NIF portugues (9 digitos) com o digito de controlo. */
export function isValidNif(nif: string): boolean {
  if (!/^[0-9]{9}$/.test(nif)) return false;
  const digits = nif.split('').map(Number);
  const sum = digits.slice(0, 8).reduce((acc, d, i) => acc + d * (9 - i), 0);
  const check = 11 - (sum % 11);
  return (check >= 10 ? 0 : check) === digits[8];
}

export const billingSchema = z.object({
  /** Nome na fatura. Vazio = consumidor final. */
  name: z
    .string()
    .trim()
    .max(120)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
  nif: z
    .string()
    .trim()
    .nullable()
    .default(null)
    .transform((v) => (v === '' || v === null ? null : v.replace(/\s/g, '')))
    .refine((v) => v === null || isValidNif(v), 'NIF invalido'),
});
export type Billing = z.infer<typeof billingSchema>;

/** Telemovel portugues para o MB WAY. */
export const mbwayPhoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, '').replace(/^(\+351|00351)/, ''))
  .refine((v) => /^9[1236][0-9]{7}$/.test(v), 'telemovel invalido para MB WAY');

export const checkoutRequestSchema = z
  .object({
    lines: z.array(cartLineSchema).min(1).max(MAX_CART_LINES),
    giftCode: giftCodeInputSchema,
    paymentMethod: onlinePaymentMethodSchema,
    mbwayPhone: mbwayPhoneSchema.nullable().default(null),
    billing: billingSchema.default({ name: null, nif: null }),
  })
  .refine((r) => r.paymentMethod !== 'mbway' || r.mbwayPhone !== null, {
    message: 'o MB WAY precisa do telemovel',
    path: ['mbwayPhone'],
  });
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/** O que quem paga tem de fazer, consoante o metodo e o prestador. */
export const paymentInstructionsSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('multibanco'), entity: z.string(), reference: z.string() }),
  z.object({ kind: z.literal('mbway'), phone: z.string() }),
  z.object({ kind: z.literal('redirect'), url: z.string().url() }),
  z.object({ kind: z.literal('in_person') }),
]);
export type PaymentInstructions = z.infer<typeof paymentInstructionsSchema>;

export const invoiceViewSchema = z.object({
  number: z.string(),
  pdfUrl: z.string().nullable(),
  issuedAt: z.string(),
});
export type InvoiceView = z.infer<typeof invoiceViewSchema>;

export const orderViewSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  channel: orderChannelSchema,
  status: orderStatusSchema,
  level: customerLevelSchema,
  paymentMethod: paymentMethodSchema,
  payment: paymentInstructionsSchema.nullable(),
  /** Ate quando o stock fica reservado a espera do pagamento (online). */
  holdUntil: z.string().nullable(),
  lines: z.array(pricedLineSchema),
  subtotalCents: z.number().int(),
  discountCents: z.number().int(),
  totalCents: z.number().int(),
  billing: z.object({ name: z.string().nullable(), nif: z.string().nullable() }),
  invoice: invoiceViewSchema.nullable(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
});
export type OrderView = z.infer<typeof orderViewSchema>;

export const orderSummarySchema = orderViewSchema.pick({
  id: true,
  number: true,
  channel: true,
  status: true,
  paymentMethod: true,
  totalCents: true,
  createdAt: true,
  paidAt: true,
});
export type OrderSummary = z.infer<typeof orderSummarySchema>;

/** O que o site precisa de saber da loja deste ambiente. */
export const storeConfigSchema = z.object({
  paymentMethods: z.array(onlinePaymentMethodSchema),
  /** Pagamentos simulados (desenvolvimento): o site mostra o botao "Simular pagamento". */
  simulatedPayments: z.boolean(),
  holdHours: z.number().int(),
});
export type StoreConfig = z.infer<typeof storeConfigSchema>;

// ---------------------------------------------------------------------------
// Back-office (Gestor de Loja)
// ---------------------------------------------------------------------------

export const adminVariantSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  sku: z.string().nullable(),
  /** `null` quando o stock nao e contado (a quota, ou stock ilimitado). */
  stock: z.number().int().nullable(),
  active: z.boolean(),
  sortOrder: z.number().int(),
  /** As opcoes da quota (F-001): preco proprio, plano e, na com camisola, qual e. */
  priceCents: z.number().int().nullable(),
  feePlan: feePlanSchema.nullable(),
  jerseyProductId: z.string().uuid().nullable(),
});
export type AdminVariant = z.infer<typeof adminVariantSchema>;

export const adminProductSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  kind: productKindSchema,
  priceCents: z.number().int(),
  associateDiscountPct: z.number().int(),
  personalization: z.boolean(),
  personalizationPriceCents: z.number().int(),
  /** A camisola que se oferece a quem faz um ano como streamer (so um produto). */
  streamerGift: z.boolean(),
  /** Parte 9 das equipas: e a camisola de alguma equipa (pode estar fora da loja). */
  teamShirt: z.boolean().default(false),
  /** Nunca esgota nem conta stock (digitais, producao em massa). Sempre na quota. */
  unlimitedStock: z.boolean(),
  audience: productAudienceSchema,
  /** A primeira foto (a capa), ou `null` sem fotos. */
  imageUrl: z.string().nullable(),
  images: z.array(z.string()),
  active: z.boolean(),
  sortOrder: z.number().int(),
  variants: z.array(adminVariantSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminProduct = z.infer<typeof adminProductSchema>;

export const imageUrlSchema = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .default(null)
  .transform((v) => (v === '' ? null : v))
  .refine((v) => v === null || /^(https:\/\/|\/)/.test(v), 'https://... ou um caminho /...');

/** As fotos de um produto: imagens carregadas (`/api/media/...`) ou URLs antigos. */
export const productImagesSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1)
      .max(500)
      .refine((v) => /^(https:\/\/|\/)/.test(v), 'https://... ou um caminho /...'),
  )
  .max(PRODUCT_IMAGES_MAX, `no maximo ${String(PRODUCT_IMAGES_MAX)} fotos`)
  .refine((list) => new Set(list).size === list.length, 'fotos repetidas')
  .default([]);

export const productInputSchema = z
  .object({
    slug: slugSchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(4000).default(''),
    kind: productKindSchema,
    priceCents: centsSchema,
    associateDiscountPct: pctSchema.default(0),
    personalization: z.boolean().default(false),
    personalizationPriceCents: centsSchema.default(0),
    streamerGift: z.boolean().default(false),
    /** Nunca esgota (F-011). Ao ligar, o stock que houver passa a zero. */
    unlimitedStock: z.boolean().default(false),
    audience: productAudienceSchema.default('all'),
    /** A primeira e a capa. */
    images: productImagesSchema,
    active: z.boolean().default(false),
    sortOrder: z.number().int().min(-1000).max(1000).default(0),
  })
  .refine((p) => p.kind !== 'membership_fee' || p.associateDiscountPct === 0, {
    message: 'a quota nao tem desconto de associado',
    path: ['associateDiscountPct'],
  })
  .refine((p) => p.kind !== 'membership_fee' || !p.personalization, {
    message: 'a quota nao tem personalizacao',
    path: ['personalization'],
  })
  .refine((p) => p.kind !== 'membership_fee' || !p.streamerGift, {
    message: 'a quota nao e oferta de streamer',
    path: ['streamerGift'],
  })
  .refine((p) => p.kind !== 'membership_fee' || p.audience === 'all', {
    message: 'a quota e para todos',
    path: ['audience'],
  });
export type ProductInput = z.infer<typeof productInputSchema>;

export const createProductRequestSchema = z.object({
  product: productInputSchema,
  /** Tamanhos. Vazio = uma variante unica ("Unico"). */
  variants: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(20),
        sku: z.string().trim().max(60).nullable().default(null),
      }),
    )
    .max(30)
    .default([]),
});
export type CreateProductRequest = z.infer<typeof createProductRequestSchema>;

export const variantInputSchema = z.object({
  label: z.string().trim().min(1).max(20),
  sku: z
    .string()
    .trim()
    .max(60)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(-1000).max(1000).default(0),
  /** So nas opcoes da quota: o preco desta opcao. */
  priceCents: centsSchema.nullable().default(null),
  /** So na quota "Anual + camisola": o produto de roupa que e a camisola. */
  jerseyProductId: z.string().uuid().nullable().default(null),
});
export type VariantInput = z.infer<typeof variantInputSchema>;

export const stockReasonSchema = z.enum(['restock', 'adjustment', 'order', 'order_cancelled']);
export type StockReason = z.infer<typeof stockReasonSchema>;

/** Entrada de mercadoria ou acerto de inventario. O stock nunca fica negativo. */
export const stockChangeRequestSchema = z.object({
  delta: z
    .number()
    .int()
    .min(-100_000)
    .max(100_000)
    .refine((d) => d !== 0, 'zero nao muda nada'),
  reason: z.enum(['restock', 'adjustment']),
  note: z.string().trim().max(300).nullable().default(null),
});
export type StockChangeRequest = z.infer<typeof stockChangeRequestSchema>;

export const stockMovementSchema = z.object({
  id: z.number().int(),
  delta: z.number().int(),
  reason: stockReasonSchema,
  orderNumber: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

/** Uma conta encontrada pelo login ou email, para a venda ao balcao. */
export const customerLookupSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  level: customerLevelSchema,
});
export type CustomerLookup = z.infer<typeof customerLookupSchema>;

/** Sugestao do campo "Cliente" da venda ao balcao: a conta, o email e o nivel. */
export const customerSuggestionSchema = customerLookupSchema.extend({
  email: z.string().nullable(),
});
export type CustomerSuggestion = z.infer<typeof customerSuggestionSchema>;

/**
 * Preco especial de uma linha ao balcao (F-013): por unidade, so mais baixo que
 * o preco normal, com motivo obrigatorio (ex.: "Preco evento").
 */
export const specialPriceSchema = z.object({
  unitPriceCents: z.number().int().min(0).max(10_000_000),
  reason: z.string().trim().min(1, 'escreva o motivo').max(80),
});
export type SpecialPrice = z.infer<typeof specialPriceSchema>;

/** Linha da venda ao balcao: a do carrinho, mais o preco especial. Online nao existe. */
export const inPersonLineSchema = cartLineSchema.extend({
  specialPrice: specialPriceSchema.nullable().default(null),
});
export type InPersonLine = z.infer<typeof inPersonLineSchema>;

/** Venda presencial (RN-E2, RN-E4). Sem conta = cliente ao balcao (nao pode levar a quota). */
export const inPersonOrderRequestSchema = z.object({
  customerAccountId: z.string().uuid().nullable().default(null),
  lines: z.array(inPersonLineSchema).min(1).max(MAX_CART_LINES),
  /** Codigo de oferta do cliente (tem de ser da conta escolhida). */
  giftCode: giftCodeInputSchema,
  paymentMethod: inPersonPaymentMethodSchema,
  /** O pagamento ja foi recebido (dinheiro na mao, MB WAY confirmado no telemovel). */
  received: z.boolean(),
  billing: billingSchema.default({ name: null, nif: null }),
});
export type InPersonOrderRequest = z.infer<typeof inPersonOrderRequestSchema>;

/** Linha vista pelo back-office: com quem aplicou o preco especial. */
export const adminOrderLineSchema = pricedLineSchema.extend({
  specialPriceBy: z.string().nullable().default(null),
});
export type AdminOrderLine = z.infer<typeof adminOrderLineSchema>;

export const adminOrderSchema = orderViewSchema.extend({
  lines: z.array(adminOrderLineSchema),
  customer: z.object({ accountId: z.string().uuid(), login: z.string().nullable() }).nullable(),
  createdBy: z.string().nullable(),
  receivedBy: z.string().nullable(),
  /** Algo a ver por uma pessoa (ex.: pagamento depois de expirar, quota por registar). */
  attention: z.string().nullable(),
  membershipSyncedAt: z.string().nullable(),
  invoiceError: z.string().nullable(),
});
export type AdminOrder = z.infer<typeof adminOrderSchema>;

export const adminOrderListQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  channel: orderChannelSchema.optional(),
  /** Numero da encomenda (GU000123). */
  q: z.string().trim().max(40).optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type AdminOrderListQuery = z.infer<typeof adminOrderListQuerySchema>;

export const adminOrderRowSchema = orderSummarySchema.extend({
  customerLogin: z.string().nullable(),
  attention: z.string().nullable(),
});
export type AdminOrderRow = z.infer<typeof adminOrderRowSchema>;

export const cancelOrderRequestSchema = z.object({
  reason: z.string().trim().max(300).nullable().default(null),
});

export const salesReportQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

// ---------------------------------------------------------------------------
// Camisola de streamer (back-office, `streaming.manage`)
// ---------------------------------------------------------------------------

/** Validade dos codigos de oferta, em dias. */
export const GIFT_CODE_VALID_DAYS = 365;

/**
 * Estado de um codigo de oferta: por usar, numa encomenda por pagar, usado
 * (encomenda paga) ou expirado sem uso.
 */
export const giftCodeStateSchema = z.enum(['available', 'in_order', 'used', 'expired']);
export type GiftCodeState = z.infer<typeof giftCodeStateSchema>;

export const giftCodeViewSchema = z.object({
  /** Formatado, XXXX-XXXX. */
  code: z.string(),
  productName: z.string(),
  state: giftCodeStateSchema,
  orderNumber: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  expiresAt: z.string(),
});
export type GiftCodeView = z.infer<typeof giftCodeViewSchema>;

/** Um streamer ativo que ja fez (ou vai fazer) um ano, e o codigo, se ja o recebeu. */
export const streamerGiftSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string().nullable(),
  channel: z.string(),
  displayName: z.string(),
  joinedAt: z.string(),
  /** Quando faz (ou fez) um ano. */
  anniversary: z.string(),
  /** Ja fez um ano como streamer ativo. */
  due: z.boolean(),
  gift: giftCodeViewSchema.nullable(),
});
export type StreamerGift = z.infer<typeof streamerGiftSchema>;

/** `GET /api/site/gifts/streamers`: quem fez um ano, com e sem codigo. */
export const streamerGiftListSchema = z.object({
  /** `false` enquanto o Stream Portal nao esta ligado (`STREAM_PORTAL_ENABLED`). */
  enabled: z.boolean(),
  /** O produto marcado como camisola de streamer, ou `null` (ainda nao ha). */
  product: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  streamers: z.array(streamerGiftSchema),
});
export type StreamerGiftList = z.infer<typeof streamerGiftListSchema>;

/** `GET /api/site/gifts/accounts/:accountId`: a ficha de um membro. */
export const accountGiftSchema = z.object({
  enabled: z.boolean(),
  /** O streamer ativo desta conta, ou `null` (nao e, ou esta em pausa). */
  streamer: streamerGiftSchema.nullable(),
  /** O codigo da camisola, se ja o recebeu (mesmo que ja nao seja streamer). */
  gift: giftCodeViewSchema.nullable(),
});
export type AccountGift = z.infer<typeof accountGiftSchema>;
